import React, { createContext, useContext, useState, useCallback } from "react";
import enTranslations from "../locales/en.json";
import ptTranslations from "../locales/pt.json";

// Available language codes for the application
type Language = "en" | "pt";

// Type for translation key strings, which can be dot-notation paths (e.g., "common.buttons.submit")
type TranslationKey = string;

// Type for translation parameters
type TranslationParams = Record<string, string | number>;

// Language context interface
interface LanguageContextType {
  // Current active language
  language: Language;
  // Function to update the current language
  setLanguage: (lang: Language) => void;
  // Translation function that returns the translated string for a given key and optional parameters
  t: (key: TranslationKey, params?: TranslationParams) => string;
}

// Object containing all translations for each supported language
const translations = {
  en: enTranslations,
  pt: ptTranslations,
};

// Context for managing the application translations
const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

/**
 * Provider component for the Language context
 */
export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguage] = useState<Language>("pt");

  // Translates a given key into the current language
  const t = useCallback(
    (key: string, params?: TranslationParams): string => {
      const keys = key.split(".");
      let value: any = translations[language];

      for (const k of keys) {
        if (value === undefined) return key;
        value = value[k];
      }

      if (!value) return key;

      // Replace parameters in the translation string if they exist
      if (params) {
        return Object.entries(params).reduce((str, [key, val]) => {
          return str.replace(new RegExp(`{${key}}`, "g"), String(val));
        }, value);
      }

      return value;
    },
    [language]
  );

  const value = {
    language,
    setLanguage,
    t,
  };

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

/**
 * Hook to access the language context
 */
export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
}
